import { useState } from "react";

import { Box, Card, CardContent, Tab, Tabs, Typography } from "@mui/material";
import { useParams } from "react-router";

import { AwsSetupPanel } from "../CloudConnectionSetupPage/AwsSetupPanel";
import { AzureSetupPanel } from "../CloudConnectionSetupPage/AzureSetupPanel";
import { GcpSetupPanel } from "../CloudConnectionSetupPage/GcpSetupPanel";
import VcsConnectionSetupPage from "../VcsConnectionSetupPage";

type ConnectionTab =
  "source-control" | "cloud-providers" | "project-management";

export default function ConnectionsPage() {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const [tab, setTab] = useState<ConnectionTab>("source-control");

  if (!orgAlias) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Organization not found in URL.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" gutterBottom>
        Connections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage source control, cloud provider, and project management
        connections for your organization.
      </Typography>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v: ConnectionTab) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="Source Control" value="source-control" />
          <Tab label="Cloud Providers" value="cloud-providers" />
          <Tab label="Project Management" value="project-management" />
        </Tabs>
        <CardContent sx={{ pt: 3 }}>
          {tab === "source-control" && <VcsConnectionSetupPage />}
          {tab === "cloud-providers" && (
            <CloudProvidersPanel organizationAlias={orgAlias} />
          )}
          {tab === "project-management" && <VcsConnectionSetupPage />}
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Cloud Providers sub-tab ──────────────────────────────────────────────────

type CloudTab = "aws" | "gcp" | "azure";

function CloudProvidersPanel({
  organizationAlias,
}: {
  organizationAlias: string;
}) {
  const [cloudTab, setCloudTab] = useState<CloudTab>("aws");

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Cloud Provider Connections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect Rusha to your cloud account using cross-account trust. No
        long-lived credentials are stored — Rusha assumes your IAM role
        on-demand.
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={cloudTab}
          onChange={(_, v: CloudTab) => setCloudTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="AWS" value="aws" />
          <Tab label="GCP" value="gcp" />
          <Tab label="Azure" value="azure" />
        </Tabs>
        <CardContent>
          {cloudTab === "aws" && (
            <AwsSetupPanel organizationAlias={organizationAlias} />
          )}
          {cloudTab === "gcp" && (
            <GcpSetupPanel organizationAlias={organizationAlias} />
          )}
          {cloudTab === "azure" && (
            <AzureSetupPanel organizationAlias={organizationAlias} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
